import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import client from '../../api/client';
import PreferredTimePicker from '../patient/PreferredTimePicker';
import {
  buildPreferredDateTimeIndia,
  fromIndiaTimeTo12HourParts,
  getEarliestAllowedIndia,
  getNowInIndia,
  to24HourTimeString,
  toIndiaApiIsoString,
  toIndiaDateInputValue,
} from '../../Utils/indiaTime';
import { parseVisitDescription } from '../../Utils/patientVisits';

const EMPTY_FORM = {
  patientId: '',
  serviceTypeId: '',
  preferredDate: '',
  timeHour: '',
  timeMinute: '',
  timePeriod: 'AM',
  symptoms: '',
  additionalNotes: '',
};

function AdminVisitFormModal({
  show,
  onHide,
  adminUserId,
  editVisit,
  patients = [],
  onSuccess,
}) {
  const isEditMode = Boolean(editVisit?.id);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    client.get('/api/service-types').then((res) => {
      if (res.data?.success) setServiceTypes(res.data.data || []);
    });
  }, [show]);

  useEffect(() => {
    if (!show) {
      setFormData(EMPTY_FORM);
      return;
    }
    if (editVisit) {
      const { symptoms, additionalNotes } = parseVisitDescription(editVisit.description);
      const preferred = editVisit.preferredDateTime ? new Date(editVisit.preferredDateTime) : null;
      const timeParts =
        preferred && !Number.isNaN(preferred.getTime())
          ? fromIndiaTimeTo12HourParts(preferred)
          : { hour: '', minute: '', period: 'AM' };
      setFormData({
        patientId: String(editVisit.patientId ?? ''),
        serviceTypeId: String(editVisit.serviceTypeId ?? ''),
        preferredDate:
          preferred && !Number.isNaN(preferred.getTime())
            ? toIndiaDateInputValue(preferred)
            : '',
        timeHour: timeParts.hour,
        timeMinute: timeParts.minute,
        timePeriod: timeParts.period,
        symptoms,
        additionalNotes,
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [show, editVisit]);

  const selectedService = useMemo(
    () => serviceTypes.find((s) => String(s.idServiceType) === String(formData.serviceTypeId)),
    [serviceTypes, formData.serviceTypeId],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditMode && !formData.patientId) {
      toast.error('Select a patient.');
      return;
    }
    if (!formData.serviceTypeId || !formData.preferredDate) {
      toast.error('Fill service and preferred date/time.');
      return;
    }

    const time24 = to24HourTimeString(formData.timeHour, formData.timeMinute, formData.timePeriod);
    if (!time24) {
      toast.error('Select preferred time.');
      return;
    }

    const preferredDateTime = buildPreferredDateTimeIndia(formData.preferredDate, time24);
    const earliest = getEarliestAllowedIndia(12);
    if (new Date(preferredDateTime) < earliest) {
      toast.error('Preferred date/time must be at least 12 hours from now (IST).');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        const res = await client.put('/api/admin/visit-requests', {
          idVisitRequest: editVisit.id,
          serviceTypeID: parseInt(formData.serviceTypeId, 10),
          preferredDateTime: toIndiaApiIsoString(preferredDateTime),
          description: formData.symptoms.trim(),
          additionalNotes: formData.additionalNotes.trim() || null,
          adminUserID: adminUserId,
        });
        if (res.data?.success) {
          toast.success('Visit request updated.');
          onSuccess?.();
          onHide();
        } else {
          toast.error(res.data?.message || 'Update failed.');
        }
      } else {
        const res = await client.post('/api/admin/visit-requests', {
          patientID: parseInt(formData.patientId, 10),
          adminUserID: adminUserId,
          serviceTypeID: parseInt(formData.serviceTypeId, 10),
          preferredDateTime: toIndiaApiIsoString(preferredDateTime),
          description: formData.symptoms.trim(),
          additionalNotes: formData.additionalNotes.trim() || null,
        });
        if (res.data?.success) {
          toast.success('Visit request created.');
          onSuccess?.();
          onHide();
        } else {
          toast.error(res.data?.message || 'Create failed.');
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const minDate = toIndiaDateInputValue(getEarliestAllowedIndia(12));
  const nowIndia = getNowInIndia();

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? 'Edit Visit Request' : 'New Visit Request'}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {!isEditMode && (
            <div className="mb-3">
              <label className="form-label">
                Patient <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={formData.patientId}
                onChange={(e) => setFormData((p) => ({ ...p, patientId: e.target.value }))}
                required
              >
                <option value="">Select patient</option>
                {patients.map((p) => (
                  <option key={p.idPatient} value={p.idPatient}>
                    {p.fullName} — {p.mobileNumber}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Service Type *</label>
              <select
                className="form-select"
                value={formData.serviceTypeId}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, serviceTypeId: e.target.value }))
                }
                required
              >
                <option value="">Select</option>
                {serviceTypes.map((s) => (
                  <option key={s.idServiceType} value={s.idServiceType}>
                    {s.serviceName}
                  </option>
                ))}
              </select>
            </div>
            {selectedService && (
              <div className="col-md-6">
                <label className="form-label">Rate</label>
                <input
                  className="form-control"
                  readOnly
                  value={`₹${Number(selectedService.defaultRateCustomer || 0).toLocaleString('en-IN')}`}
                />
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label">Preferred Date (IST) *</label>
              <input
                type="date"
                className="form-control"
                min={minDate}
                value={formData.preferredDate}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, preferredDate: e.target.value }))
                }
                required
              />
            </div>
            <div className="col-12">
              <PreferredTimePicker
                hour={formData.timeHour}
                minute={formData.timeMinute}
                period={formData.timePeriod}
                onChange={(parts) => setFormData((p) => ({ ...p, ...parts }))}
                label="Preferred Time (IST 🇮🇳) *"
              />
            </div>
            <div className="col-12">
              <label className="form-label">Symptoms / Description *</label>
              <textarea
                className="form-control"
                rows={3}
                value={formData.symptoms}
                onChange={(e) => setFormData((p) => ({ ...p, symptoms: e.target.value }))}
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label">Additional Notes</label>
              <textarea
                className="form-control"
                rows={2}
                value={formData.additionalNotes}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, additionalNotes: e.target.value }))
                }
              />
            </div>
          </div>
          <p className="text-muted small mt-2">
            Minimum 12 hours from now ({nowIndia.toLocaleString('en-IN')})
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onHide}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default AdminVisitFormModal;
